import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { PaymentMethod, NewPaymentMethod } from '../types';

interface PaymentMethodStore {
  paymentMethods: PaymentMethod[];
  loading: boolean;
  error: string | null;
  showArchived: boolean;
  fetchPaymentMethods: () => Promise<void>;
  createPaymentMethod: (method: NewPaymentMethod) => Promise<PaymentMethod | null>;
  updatePaymentMethod: (id: string, method: Partial<PaymentMethod>) => Promise<PaymentMethod | null>;
  deletePaymentMethod: (id: string) => Promise<boolean>;
  archivePaymentMethod: (id: string) => Promise<boolean>;
  unarchivePaymentMethod: (id: string) => Promise<boolean>;
  toggleShowArchived: () => void;
}

export const usePaymentMethodStore = create<PaymentMethodStore>((set, get) => ({
  paymentMethods: [],
  loading: false,
  error: null,
  showArchived: false,

  fetchPaymentMethods: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;

      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      let query = supabase
        .from('payment_methods')
        .select('*, payment_schedule, payment_type')
        .eq('organization_id', profileData.organization_id);

      if (!get().showArchived) {
        query = query.or('archived.is.null,archived.eq.false');
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      set({ paymentMethods: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createPaymentMethod: async (method) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // Add organization_id to method data
      const methodData = {
        ...method,
        organization_id: profileData.organization_id
      };

      const { data, error } = await supabase
        .from('payment_methods')
        .insert(methodData)
        .select('*, payment_schedule, payment_type')
        .single();

      if (error) throw error;
      
      const paymentMethods = get().paymentMethods;
      set({ paymentMethods: [data, ...paymentMethods] });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updatePaymentMethod: async (id, method) => {
    try {
      // Get current user's organization_id for security
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('payment_methods')
        .update(method)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .select('*, payment_schedule, payment_type')
        .single();

      if (error) throw error;

      const paymentMethods = get().paymentMethods.map(m => 
        m.id === id ? { ...m, ...data } : m
      );
      set({ paymentMethods });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deletePaymentMethod: async (id) => {
    try {
      // Get current user's organization_id for security
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { error } = await supabase
        .from('payment_methods')
        .delete()
        .eq('id', id)
        .eq('organization_id', profileData.organization_id);

      if (error) throw error;

      const paymentMethods = get().paymentMethods.filter(m => m.id !== id);
      set({ paymentMethods });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  archivePaymentMethod: async (id) => {
    const result = await get().updatePaymentMethod(id, { archived: true });
    if (!result) return false;
    if (!get().showArchived) {
      set(state => ({
        paymentMethods: state.paymentMethods.filter(m => m.id !== id),
      }));
    } else {
      set(state => ({
        paymentMethods: state.paymentMethods.map(m =>
          m.id === id ? { ...m, archived: true } : m
        ),
      }));
    }
    return true;
  },

  unarchivePaymentMethod: async (id) => {
    const result = await get().updatePaymentMethod(id, { archived: false });
    if (!result) return false;
    set(state => ({
      paymentMethods: state.paymentMethods.map(m =>
        m.id === id ? { ...m, archived: false } : m
      ),
    }));
    return true;
  },

  toggleShowArchived: () => {
    set(state => ({ showArchived: !state.showArchived }));
    get().fetchPaymentMethods();
  },
}));